import { apiUpload } from "@d12labs/client";
import { Button } from "@d12labs/ui/button";
import { ConfirmDialog } from "@d12labs/ui/confirm-dialog";
import { EmptyState } from "@d12labs/ui/empty-state";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Skeleton } from "@d12labs/ui/skeleton";
import { formatBytes } from "@d12labs/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Cloud, Download, FileText, Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
// Shapes derive from the server's route defs (Phase 7) — no hand-typed UploadRecord.
import { type UploadRecord, uploadsApi } from "../api";

type PendingUpload = {
	localId: string;
	name: string;
	size: number;
	status: "uploading" | "error";
	error?: string;
};

// Multipart POST through apiUpload — the route declares no input schema and
// typedRoute is JSON-only.
function uploadOne(file: File): Promise<UploadRecord> {
	const formData = new FormData();
	formData.append("file", file);
	return apiUpload<UploadRecord>("/api/plugins/uploads/files", formData);
}

export function UploadsPage() {
	const queryClient = useQueryClient();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [pending, setPending] = useState<PendingUpload[]>([]);
	const [dragging, setDragging] = useState(false);

	const { data: files, isLoading } = useQuery({
		queryKey: ["uploads"],
		queryFn: () => uploadsApi.list(),
	});

	const deleteFile = useMutation({
		mutationFn: (id: string) => uploadsApi.remove({ params: { id } }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["uploads"] });
			toast.success("File deleted");
		},
	});

	const handleDownload = async (id: string) => {
		try {
			const { url } = await uploadsApi.url({ params: { id } });
			window.open(url, "_blank");
		} catch (err) {
			toast.error((err as Error).message);
		}
	};

	const startUploads = (fileList: FileList | File[]) => {
		const list = Array.from(fileList);
		if (list.length === 0) return;

		const entries: PendingUpload[] = list.map((f) => ({
			localId: crypto.randomUUID(),
			name: f.name,
			size: f.size,
			status: "uploading",
		}));
		setPending((prev) => [...prev, ...entries]);

		entries.forEach((entry, i) => {
			const file = list[i];
			if (!file) return;
			uploadOne(file)
				.then(() => {
					setPending((prev) => prev.filter((p) => p.localId !== entry.localId));
					queryClient.invalidateQueries({ queryKey: ["uploads"] });
				})
				.catch((err: Error) => {
					setPending((prev) =>
						prev.map((p) =>
							p.localId === entry.localId ? { ...p, status: "error", error: err.message } : p,
						),
					);
					toast.error(`${file.name}: ${err.message}`);
				});
		});
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (files && files.length > 0) startUploads(files);
		// Reset so re-picking the same file triggers change.
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setDragging(false);
		const files = e.dataTransfer.files;
		if (files.length > 0) startUploads(files);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		if (!dragging) setDragging(true);
	};

	const dismissPending = (localId: string) => {
		setPending((prev) => prev.filter((p) => p.localId !== localId));
	};

	const fileCount = files?.length ?? 0;

	return (
		<>
			<PageHeader
				icon={Cloud}
				title="Uploads"
				subtitle={
					fileCount > 0
						? `${fileCount} file${fileCount === 1 ? "" : "s"}`
						: "Drop files to get started"
				}
				actions={
					<Button onClick={() => fileInputRef.current?.click()}>
						<Upload className="size-4 mr-1.5" />
						Upload
					</Button>
				}
			/>
			<PageBody className="p-6">
				<div className="mx-auto w-full max-w-5xl space-y-5">
					{/* Hidden file input */}
					<input
						ref={fileInputRef}
						type="file"
						multiple
						onChange={handleFileChange}
						className="hidden"
					/>

					{/* Dropzone */}
					<button
						type="button"
						onClick={() => fileInputRef.current?.click()}
						onDrop={handleDrop}
						onDragOver={handleDragOver}
						onDragLeave={() => setDragging(false)}
						className={[
							"flex w-full items-center gap-3 rounded-lg border border-dashed p-5 text-left transition-colors cursor-pointer",
							dragging
								? "border-primary bg-primary/5 text-foreground"
								: "border-hairline-strong bg-surface-1/40 hover:bg-surface-1 hover:border-hairline",
						].join(" ")}
					>
						<span className="flex size-9 flex-none items-center justify-center rounded-md border border-hairline bg-surface-2 text-ink-muted">
							<Upload className="size-4" />
						</span>
						<div className="flex-1">
							<div className="text-body-sm font-medium text-foreground">Drop files anywhere</div>
							<div className="text-meta text-ink-subtle">
								Up to 25 MB each · JPG, PNG, PDF, DOCX
							</div>
						</div>
						<Button
							type="button"
							variant="secondary"
							size="sm"
							onClick={(e) => {
								e.stopPropagation();
								fileInputRef.current?.click();
							}}
						>
							Choose files
						</Button>
					</button>

					{/* Pending uploads */}
					{pending.length > 0 ? (
						<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
							{pending.map((p) => (
								<div
									key={p.localId}
									className="flex flex-col rounded-lg border border-hairline bg-surface-1 overflow-hidden"
								>
									<div className="relative aspect-[4/3] flex flex-col items-center justify-center gap-1.5 bg-surface-2">
										<Upload className="size-5 text-primary" />
										{p.status === "uploading" ? (
											<>
												<div className="h-1 w-20 rounded-full bg-surface-3 overflow-hidden">
													<div className="h-full bg-primary animate-pulse w-1/2" />
												</div>
												<span className="text-meta text-ink-subtle">Uploading…</span>
											</>
										) : (
											<span className="text-meta text-destructive">Failed</span>
										)}
									</div>
									<div className="flex items-center gap-2 p-2.5 border-t border-hairline">
										<div className="min-w-0 flex-1">
											<div className="truncate text-body-sm text-foreground">{p.name}</div>
											<div className="text-meta text-ink-subtle">{formatBytes(p.size)}</div>
										</div>
										{p.status === "error" ? (
											<Button
												type="button"
												variant="ghost"
												size="icon-sm"
												onClick={() => dismissPending(p.localId)}
												aria-label="Dismiss"
											>
												<span className="text-meta">✕</span>
											</Button>
										) : null}
									</div>
								</div>
							))}
						</div>
					) : null}

					{/* Files grid */}
					{isLoading ? (
						<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
							{Array.from({ length: 10 }).map((_, i) => (
								<div
									// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
									key={i}
									className="flex flex-col rounded-lg border border-hairline bg-surface-1 overflow-hidden"
								>
									<div className="aspect-[4/3] w-full animate-pulse bg-surface-2" />
									<div className="p-2.5 space-y-1.5 border-t border-hairline">
										<Skeleton w="80%" h={12} />
										<Skeleton w="45%" h={10} />
									</div>
								</div>
							))}
						</div>
					) : files && files.length > 0 ? (
						<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
							{files.map((file) => (
								<div
									key={file.id}
									className="group flex flex-col rounded-lg border border-hairline bg-surface-1 overflow-hidden hover:border-hairline-strong transition-colors"
								>
									{/* Thumbnail / icon area */}
									<div className="relative aspect-[4/3] flex items-center justify-center overflow-hidden bg-surface-2">
										{file.previewUrl ? (
											<img
												src={file.previewUrl}
												alt={file.originalName}
												className="size-full object-cover"
												loading="lazy"
											/>
										) : (
											<FileText className="size-7 text-ink-subtle" />
										)}
									</div>

									{/* File info + actions */}
									<div className="flex items-center gap-2 p-2.5 border-t border-hairline">
										<div className="min-w-0 flex-1">
											<div className="truncate text-body-sm text-foreground">
												{file.originalName}
											</div>
											<div className="text-meta text-ink-subtle">{formatBytes(file.size)}</div>
										</div>
										<div className="flex gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
											<Button
												variant="ghost"
												size="icon-sm"
												onClick={() => handleDownload(file.id)}
												aria-label="Download"
											>
												<Download className="size-3.5" />
											</Button>
											<ConfirmDialog
												title="Delete this file?"
												description={`"${file.originalName}" will be permanently removed from storage.`}
												confirmLabel="Delete"
												destructive
												onConfirm={() => deleteFile.mutate(file.id)}
											>
												<Button variant="ghost" size="icon-sm" aria-label="Delete">
													<Trash2 className="size-3.5" />
												</Button>
											</ConfirmDialog>
										</div>
									</div>
								</div>
							))}
						</div>
					) : pending.length === 0 ? (
						<EmptyState
							icon={Cloud}
							title="No uploads yet"
							body="Drop files above or click Upload to add your first file."
							primary={
								<Button onClick={() => fileInputRef.current?.click()}>
									<Upload className="size-4 mr-1.5" />
									Upload files
								</Button>
							}
						/>
					) : null}
				</div>
			</PageBody>
		</>
	);
}
